/**
 * Export dropdown for the Daily Photo List.
 *
 * Thin wrapper around SheetSelectionDropdown that triggers XLSX download.
 */
import React, { useCallback, useState } from "react"
import { Download } from "lucide-react"

import { downloadPhotoListXlsx } from "../../api/photoList"
import SheetSelectionDropdown from "./SheetSelectionDropdown"

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

function formatExportFilename(runStartedAt: string | null): string {
  const pad = (n: number) => n.toString().padStart(2, "0")
  const d = runStartedAt ? new Date(runStartedAt) : new Date()
  return `INVI_Photo_List_${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${d.getFullYear()}.xlsx`
}

interface Props {
  currentRunId: number | null
  runStartedAt: string | null
  /** Active dealer group — the export returns that group's latest run. */
  dealerGroupId: number | null
  /** Fill the row (mobile header) — passed through to SheetSelectionDropdown. */
  fullWidth?: boolean
}

export default function ExportDropdown({
  currentRunId,
  runStartedAt,
  dealerGroupId,
  fullWidth = false,
}: Props) {
  const [downloading, setDownloading] = useState(false)

  const handleDownload = useCallback(
    async (selectedSheets: string[], sheetOrder: string[]) => {
      if (!currentRunId) return
      setDownloading(true)
      try {
        const blob = await downloadPhotoListXlsx(selectedSheets, sheetOrder, dealerGroupId)
        triggerBrowserDownload(blob, formatExportFilename(runStartedAt))
      } finally {
        setDownloading(false)
      }
    },
    [currentRunId, runStartedAt, dealerGroupId],
  )

  return (
    <SheetSelectionDropdown
      triggerLabel="Export"
      leadingIcon={<Download size={14} strokeWidth={2} />}
      busyLabel="Preparing..."
      actionLabel="Download XLSX"
      onAction={handleDownload}
      disabled={currentRunId === null}
      busy={downloading}
      fullWidth={fullWidth}
    />
  )
}
